<template>
  <div id="appNavbar">
    <div class="hidden-md-and-down app-header-desktop">
      <!-- Top Row: Menu Toggle, Search Bar, Date Badge, Notifications, User Profile -->
      <div class="header-top-bar d-flex align-center justify-space-between px-4 py-3">
        <div class="header-left d-flex align-center">
          <v-btn icon small class="mr-3 sidebar-toggle-btn" @click="$root.$emit('toggle-side-nav')" title="Toggle Menu">
            <v-icon color="#555e6c" size="22">mdi-menu</v-icon>
          </v-btn>
          <div v-if="!isSuperAdmin" class="header-search-wrapper" ref="searchContainer">
            <div class="header-search-container" :class="{ 'is-focused': isSearchOpen }">
              <v-icon size="18" :color="isSearchOpen ? '#9a6b27' : '#8d8a98'" class="mr-2">mdi-magnify</v-icon>
              <input
                type="text"
                ref="headerSearchInput"
                v-model="searchQuery"
                @focus="openSearch"
                @input="onSearchInput"
                @keydown.down.prevent="navigateResults(1)"
                @keydown.up.prevent="navigateResults(-1)"
                @keydown.enter.prevent="selectCurrentResult"
                @keydown.esc="closeSearch"
                placeholder="Search invoice, customer, stock, product, etc..."
                class="header-search-field"
              />
              <v-progress-circular
                v-if="isSearchingLive"
                indeterminate
                size="15"
                width="2"
                color="#9a6b27"
                class="mr-2"
              ></v-progress-circular>
              <v-btn
                v-else-if="searchQuery"
                icon
                x-small
                @click="clearSearch"
                class="mr-1 clear-search-btn"
                title="Clear search"
              >
                <v-icon size="14" color="#94a3b8">mdi-close</v-icon>
              </v-btn>
              <span class="search-kbd-shortcut" v-else>Ctrl K</span>
            </div>

            <!-- Modern Search Popover / Command Palette -->
            <transition name="search-popover-fade">
              <div v-if="isSearchOpen" class="search-results-popover" @mousedown.prevent>
                <!-- Initial Suggestions when query is empty -->
                <div v-if="!searchQuery.trim()" class="search-suggestions-container">
                  <div class="search-group-header d-flex align-center justify-space-between">
                    <span>QUICK NAVIGATION</span>
                    <span class="hint-text">Frequently Used</span>
                  </div>
                  <div class="search-results-list">
                    <div
                      v-for="(item, idx) in quickSuggestions"
                      :key="'sugg-' + idx"
                      class="search-result-item d-flex align-center justify-space-between"
                      :class="{ 'is-active': selectedSearchIndex === idx }"
                      @click="executeResult(item)"
                      @mouseenter="selectedSearchIndex = idx"
                    >
                      <div class="d-flex align-center overflow-hidden">
                        <div class="result-icon-pill mr-3" :style="{ backgroundColor: item.iconBg || '#f8fafc' }">
                          <v-icon size="16" :color="item.iconColor || '#9a6b27'">{{ item.icon }}</v-icon>
                        </div>
                        <div class="result-text-block">
                          <div class="result-title">{{ item.title }}</div>
                          <div class="result-subtitle">{{ item.subtitle }}</div>
                        </div>
                      </div>
                      <div class="d-flex align-center">
                        <span class="result-badge">{{ item.badge }}</span>
                        <v-icon v-if="selectedSearchIndex === idx" size="14" color="#9a6b27" class="ml-2">mdi-keyboard-return</v-icon>
                      </div>
                    </div>
                  </div>

                  <!-- Quick Rates Glance -->
                  <div v-if="!isSuperAdmin" class="search-rates-preview mt-3 pt-3 border-top">
                    <div class="search-group-header mb-2">CURRENT BOARDING RATES</div>
                    <div class="d-flex flex-wrap gap-2 rates-chips-row">
                      <div class="rate-chip" @click="navigateTo('/BoardingRate')">
                        <span class="rate-chip-metal">Gold:</span>
                        <span class="rate-chip-price">₹{{ getStoredRate('goldRate', '10,000') }}/g</span>
                      </div>
                      <div class="rate-chip" @click="navigateTo('/BoardingRate')">
                        <span class="rate-chip-metal">Thankam:</span>
                        <span class="rate-chip-price">₹{{ getStoredRate('thankamRate', '10,000') }}/g</span>
                      </div>
                      <div class="rate-chip" @click="navigateTo('/BoardingRate')">
                        <span class="rate-chip-metal">Silver:</span>
                        <span class="rate-chip-price">₹{{ getStoredRate('silverRate', '10,000') }}/g</span>
                      </div>
                      <div class="rate-chip" @click="navigateTo('/BoardingRate')">
                        <span class="rate-chip-metal">Platinum:</span>
                        <span class="rate-chip-price">₹{{ getStoredRate('platinumRate', '10,000') }}/g</span>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Active Search Results -->
                <div v-else class="search-active-results">
                  <!-- No Results State -->
                  <div v-if="flattenedResults.length === 0 && !isSearchingLive" class="search-empty-state text-center py-6 px-4">
                    <div class="search-empty-icon mb-2">
                      <v-icon size="32" color="#cbd5e1">mdi-file-search-outline</v-icon>
                    </div>
                    <div class="search-empty-title">No results found for "{{ searchQuery }}"</div>
                    <div class="search-empty-sub">Try searching with a bill number (e.g. 1013), customer name, or feature name</div>
                  </div>

                  <!-- Matching Groups -->
                  <template v-else>
                    <!-- Group 1: Boarding Rate Match -->
                    <div v-if="filteredRates.length > 0" class="search-group mb-2">
                      <div class="search-group-header">BOARDING RATES</div>
                      <div
                        v-for="item in filteredRates"
                        :key="'rate-' + item.metal"
                        class="search-result-item d-flex align-center justify-space-between"
                        :class="{ 'is-active': selectedSearchIndex === getResultIndex(item) }"
                        @click="executeResult(item)"
                        @mouseenter="selectedSearchIndex = getResultIndex(item)"
                      >
                        <div class="d-flex align-center">
                          <div class="result-icon-pill mr-3" style="background-color: #fffbeb;">
                            <v-icon size="16" color="#d97706">mdi-gold</v-icon>
                          </div>
                          <div class="result-text-block">
                            <div class="result-title">{{ item.title }}</div>
                            <div class="result-subtitle">{{ item.subtitle }}</div>
                          </div>
                        </div>
                        <div class="d-flex align-center">
                          <span class="result-rate-val font-weight-bold mr-2">{{ item.rate }}</span>
                          <span class="result-badge badge-rate">Rate Master</span>
                        </div>
                      </div>
                    </div>

                    <!-- Group 2: Purchase Invoices / Bills -->
                    <div v-if="liveBills.length > 0" class="search-group mb-2">
                      <div class="search-group-header d-flex align-center justify-space-between">
                        <span>PURCHASE BILLS & INVOICES</span>
                        <span class="badge-count">{{ liveBills.length }}</span>
                      </div>
                      <div
                        v-for="bill in liveBills"
                        :key="'bill-' + bill.id"
                        class="search-result-item d-flex align-center justify-space-between"
                        :class="{ 'is-active': selectedSearchIndex === getResultIndex(bill) }"
                        @click="executeResult(bill)"
                        @mouseenter="selectedSearchIndex = getResultIndex(bill)"
                      >
                        <div class="d-flex align-center overflow-hidden">
                          <div class="result-icon-pill mr-3" style="background-color: #fff7ed;">
                            <v-icon size="16" color="#9a6b27">mdi-receipt-text-outline</v-icon>
                          </div>
                          <div class="result-text-block">
                            <div class="result-title">{{ bill.title }}</div>
                            <div class="result-subtitle">{{ bill.subtitle }}</div>
                          </div>
                        </div>
                        <div class="d-flex align-center">
                          <span class="result-status-pill mr-2" :class="bill.isSettled ? 'status-settled' : 'status-unsettled'">
                            {{ bill.isSettled ? 'Settled' : 'Pending' }}
                          </span>
                          <v-icon v-if="selectedSearchIndex === getResultIndex(bill)" size="14" color="#9a6b27">mdi-keyboard-return</v-icon>
                        </div>
                      </div>
                    </div>

                    <!-- Group 3: Suppliers -->
                    <div v-if="liveSuppliers.length > 0" class="search-group mb-2">
                      <div class="search-group-header d-flex align-center justify-space-between">
                        <span>SUPPLIERS</span>
                        <span class="badge-count">{{ liveSuppliers.length }}</span>
                      </div>
                      <div
                        v-for="sup in liveSuppliers"
                        :key="'sup-' + sup.id"
                        class="search-result-item d-flex align-center justify-space-between"
                        :class="{ 'is-active': selectedSearchIndex === getResultIndex(sup) }"
                        @click="executeResult(sup)"
                        @mouseenter="selectedSearchIndex = getResultIndex(sup)"
                      >
                        <div class="d-flex align-center overflow-hidden">
                          <div class="result-icon-pill mr-3" style="background-color: #ecfeff;">
                            <v-icon size="16" color="#0891b2">mdi-store-outline</v-icon>
                          </div>
                          <div class="result-text-block">
                            <div class="result-title">{{ sup.title }}</div>
                            <div class="result-subtitle">{{ sup.subtitle }}</div>
                          </div>
                        </div>
                        <div class="d-flex align-center">
                          <span class="result-badge badge-supplier">Supplier</span>
                          <v-icon v-if="selectedSearchIndex === getResultIndex(sup)" size="14" color="#9a6b27" class="ml-2">mdi-keyboard-return</v-icon>
                        </div>
                      </div>
                    </div>

                    <!-- Group 4: Customers -->
                    <div v-if="liveCustomers.length > 0" class="search-group mb-2">
                      <div class="search-group-header d-flex align-center justify-space-between">
                        <span>CUSTOMERS</span>
                        <span class="badge-count">{{ liveCustomers.length }}</span>
                      </div>
                      <div
                        v-for="cust in liveCustomers"
                        :key="'cust-' + cust.id"
                        class="search-result-item d-flex align-center justify-space-between"
                        :class="{ 'is-active': selectedSearchIndex === getResultIndex(cust) }"
                        @click="executeResult(cust)"
                        @mouseenter="selectedSearchIndex = getResultIndex(cust)"
                      >
                        <div class="d-flex align-center overflow-hidden">
                          <div class="result-icon-pill mr-3" style="background-color: #fdf2f8;">
                            <v-icon size="16" color="#db2777">mdi-account-heart-outline</v-icon>
                          </div>
                          <div class="result-text-block">
                            <div class="result-title">{{ cust.title }}</div>
                            <div class="result-subtitle">{{ cust.subtitle }}</div>
                          </div>
                        </div>
                        <div class="d-flex align-center">
                          <span class="result-badge badge-customer">Customer</span>
                          <v-icon v-if="selectedSearchIndex === getResultIndex(cust)" size="14" color="#9a6b27" class="ml-2">mdi-keyboard-return</v-icon>
                        </div>
                      </div>
                    </div>

                    <!-- Group 5: Pages & Modules -->
                    <div v-if="filteredPages.length > 0" class="search-group mb-2">
                      <div class="search-group-header d-flex align-center justify-space-between">
                        <span>PAGES & ACTIONS</span>
                        <span class="badge-count">{{ filteredPages.length }}</span>
                      </div>
                      <div
                        v-for="page in filteredPages"
                        :key="'page-' + page.route"
                        class="search-result-item d-flex align-center justify-space-between"
                        :class="{ 'is-active': selectedSearchIndex === getResultIndex(page) }"
                        @click="executeResult(page)"
                        @mouseenter="selectedSearchIndex = getResultIndex(page)"
                      >
                        <div class="d-flex align-center overflow-hidden">
                          <div class="result-icon-pill mr-3" :style="{ backgroundColor: page.iconBg || '#f8fafc' }">
                            <v-icon size="16" :color="page.iconColor || '#9a6b27'">{{ page.icon }}</v-icon>
                          </div>
                          <div class="result-text-block">
                            <div class="result-title">{{ page.title }}</div>
                            <div class="result-subtitle">{{ page.subtitle }}</div>
                          </div>
                        </div>
                        <div class="d-flex align-center">
                          <span class="result-badge">{{ page.badge }}</span>
                          <v-icon v-if="selectedSearchIndex === getResultIndex(page)" size="14" color="#9a6b27" class="ml-2">mdi-keyboard-return</v-icon>
                        </div>
                      </div>
                    </div>
                  </template>
                </div>

                <!-- Footer / Keyboard Shortcut Guide -->
                <div class="search-popover-footer d-flex align-center justify-space-between px-3 py-2 border-top">
                  <div class="d-flex align-center gap-2 footer-hints">
                    <span class="kbd-hint"><kbd>↑</kbd><kbd>↓</kbd> navigate</span>
                    <span class="kbd-hint"><kbd>↵</kbd> select</span>
                    <span class="kbd-hint"><kbd>esc</kbd> close</span>
                  </div>
                  <span class="footer-count text-muted" v-if="searchQuery.trim()">
                    {{ flattenedResults.length }} result{{ flattenedResults.length === 1 ? '' : 's' }}
                  </span>
                </div>
              </div>
            </transition>
          </div>
        </div>

        <div class="header-right d-flex align-center">
          <div class="header-calendar-pill mr-4 d-flex align-center">
            <v-icon size="18" color="#64748b" class="mr-2">mdi-calendar-month-outline</v-icon>
            <div class="calendar-date-info d-flex flex-column text-left">
              <span class="calendar-day-name">{{ currentDayName }}</span>
              <span class="calendar-full-date">{{ currentFormattedDate }}</span>
            </div>
          </div>

          <AccountMenu />
        </div>
      </div>

      <!-- Second Row: Boarding Rates Strip -->
      <div v-if="!isSuperAdmin" class="header-rates-strip px-4 py-2">
        <Price />
      </div>
    </div>
    <v-layout wrap class="hidden-lg-and-up">
      <v-flex xs12>
        <v-app-bar
          app
          color="#ffffff"
          dense
          flat 
          height="54px"
          class="hidden-lg-and-up mobile-app-bar"
        >
          <div class="d-flex align-center justify-space-between" style="width: 100%;">
            <div class="d-flex align-center">
              <v-btn icon small @click.stop="sideNav = !sideNav" title="Toggle Menu" class="mr-1">
                <v-icon color="#1e293b" size="24">mdi-menu</v-icon>
              </v-btn>
              <router-link :to="dashboardRoute" style="text-decoration: none; display: flex; align-items: center;">
                <span class="mobile-logo-text">JEWEL<span class="gold-text">BIZ</span></span>
                <span class="demo-badge-header ml-2">Demo</span>
              </router-link>
            </div>
            <div class="d-flex align-center">
              <AccountMenu />
            </div>
          </div>
        </v-app-bar>
      </v-flex>

      <!-- Mobile Boarding Rates Strip (Horizontal swipe) -->
      <v-flex v-if="!isSuperAdmin" xs12 class="mobile-rates-strip-container px-2 py-2">
        <Price />
      </v-flex>

      <!-- Mobile Navigation Drawer -->
      <v-navigation-drawer
        v-model="sideNav"
        fixed
        temporary
        overlay-color="#000000"
        overlay-opacity="0.4"
        width="280"
        class="mobile-nav-drawer"
      >
        <div class="sidebar-brand-wrapper text-left pl-6 pt-6 pb-4 position-relative">
          <router-link :to="dashboardRoute" class="sidebar-logo-link" style="text-decoration: none;">
            <span class="sidebar-logo-main">
              <span class="sidebar-logo-text">JEWEL<span class="gold-text">BIZ</span></span>
            </span>
            <div class="sidebar-tagline">BRILLIANCE IN BUSINESS</div>
          </router-link>
        </div>

        <v-layout wrap align-start class="sidebar-nav-container">
          <!-- Staff: Dashboard is not part of the server-provided menu (same as desktop sidebar) -->
          <v-flex v-if="appType == 'Staff' && !staffMenuHasDashboard" xs12 px-3 pb-1>
            <router-link :to="dashboardRoute" style="text-decoration: none;" @click.native="sideNav = false">
              <v-list-item class="rounded-pill" :class="{ 'active-menu-pill': isRouteActive('/StaffDashboard') }">
                <v-list-item-icon class="mr-3 my-0 align-self-center">
                  <v-icon small :color="isRouteActive('/StaffDashboard') ? '#9a6b27' : '#526071'">mdi-home</v-icon>
                </v-list-item-icon>
                <v-list-item-title>
                  <span class="text-none menu-item-text" :class="{ 'active-text-orange': isRouteActive('/StaffDashboard') }">Dashboard</span>
                </v-list-item-title>
              </v-list-item>
            </router-link>
          </v-flex>
          <v-flex xs12 text-left text-uppercase v-if="navItems">
            <v-list dense v-for="(main, i) in navItems" :key="i" class="subhed py-0">
              <v-layout wrap v-if="main && main.menu">
                <v-flex xs12 py-1 px-3>
                  <!-- Menus with sub-items must not close the drawer when tapped, otherwise they can never be expanded -->
                  <component
                    :is="hasSubItems(main) ? 'div' : 'router-link'"
                    :to="hasSubItems(main) ? undefined : main.menu.route"
                    style="text-decoration: none;"
                    @click.native="!hasSubItems(main) && (sideNav = false)"
                  >
                    <v-list-group no-action :value="isMobileGroupActive(main)" active-class="activated" :class="{ 'active-menu-pill': isMobileGroupActive(main) }">
                      <template v-slot:activator>
                        <v-list-item-icon class="mr-3 my-0 align-self-center">
                          <v-icon small :color="isMobileGroupActive(main) ? '#9a6b27' : '#526071'">
                            {{ getMenuIcon(main.menu.name) }}
                          </v-icon>
                        </v-list-item-icon>
                        <v-list-item-title>
                          <span class="text-none menu-item-text" :class="{ 'active-text-orange': isMobileGroupActive(main) }">
                            {{ formatMenuName(main.menu.name) }}
                          </span>
                        </v-list-item-title>
                      </template>
                      <div v-if="main.menu.subMenu" class="subhed">
                        <template v-for="(sub, sIdx) in main.menu.subMenu">
                          <v-flex xs12 text-left pl-6 py-1 :key="sIdx">
                            <router-link :to="sub.route" style="text-decoration: none;" @click.native="sideNav = false">
                              <span class="text-none" style="font-size: 12px; color: #526071;">
                                -> {{ sub.name }}
                              </span>
                            </router-link>
                          </v-flex>
                        </template>
                      </div>
                      <div v-if="main.menu.subMenuCategories" class="report-categories-container">
                        <v-list-group
                          v-for="(cat, cIndex) in main.menu.subMenuCategories"
                          :key="'cat-' + cIndex"
                          :value="cat.reports.some((r) => r.route === $route.path)"
                          sub-group
                          no-action
                          class="report-sub-group"
                        >
                          <template v-slot:activator>
                            <v-list-item-title class="report-cat-title-wrapper">
                              <span class="report-cat-title">{{ formatMenuName(cat.categoryName) }}</span>
                            </v-list-item-title>
                          </template>
                          <div class="report-sub-items-tree">
                            <router-link
                              v-for="(sub, sIdx) in cat.reports"
                              :key="'sub-' + sIdx"
                              :to="sub.route"
                              class="report-sub-link d-flex align-start text-decoration-none"
                              :class="{ 'report-sub-link-active': $route.path == sub.route }"
                              @click.native="sideNav = false"
                            >
                              <span class="report-bullet-dot"></span>
                              <span class="report-link-text">{{ formatMenuName(sub.name) }}</span>
                            </router-link>
                          </div>
                        </v-list-group>
                      </div>
                    </v-list-group>
                  </component>
                </v-flex>
              </v-layout>
            </v-list>
          </v-flex>
        </v-layout>

        <!-- Bottom Badge -->
        <div class="sidebar-bottom-section px-4 pt-4 pb-5 mt-auto">
          <div class="bottom-jewellers-card d-flex align-center">
            <div class="bottom-card-icon-wrapper mr-2">
              <v-icon color="#9a6b27" size="26">mdi-diamond-outline</v-icon>
            </div>
            <div class="badge-text text-left">
              <div class="badge-line1">Crafting</div>
              <div class="badge-line2">Growth Together</div>
            </div>
          </div>
        </div>
      </v-navigation-drawer>
    </v-layout>
  </div>
</template>
<script>
import axios from "axios";
import AccountMenu from "./../components/accountMenu";
import Price from "./../components/PricePage.vue";
import adminMenu from "./adminMenu";
// import SwitchAccount from "./../components/switchAccount";
export default {
  components: {
    AccountMenu,
    Price,
    // SwitchAccount
  },
  data() {
    return {
      sideNav: false,
      admin: [{ name: "ADMINS", route: "/Admins" }],
      menu2: [],
      userNavItemsSuperAdmin: [
        {
          menu: {
            name: "Jewellery Admin",
            route: "/dashboard",
          },
        },
        {
          menu: {
            name: "Jewellery Stores",
            route: "/ViewAdmin",
          },
        },
        {
          menu: {
            name: "Platform Admins",
            route: "/PlatformAdmins",
          },
        },
        {
          menu: {
            name: "Audit Log",
            route: "/SuperAdminAuditLog",
          },
        },
        {
          menu: {
            name: "Menu Master",
            route: "/MenuMaster",
          },
        },
      ],

      userNavItemsJuwelAdmin: adminMenu,
      userNavItemsStaffAdmin: [],
      newGSTstatus: "",
      // Global Search State
      searchQuery: "",
      isSearchOpen: false,
      isSearchingLive: false,
      selectedSearchIndex: 0,
      searchDebounceTimer: null,
      liveBills: [],
      liveSuppliers: [],
      liveCustomers: [],
      quickSuggestions: [
        {
          type: "page",
          title: "New Purchase Entry",
          subtitle: "Record jewelry stock, invoice details & touch rates",
          route: "/Purchase",
          icon: "mdi-cart-plus",
          iconBg: "#fff7ed",
          iconColor: "#9a6b27",
          badge: "Purchase",
        },
        {
          type: "page",
          title: "Purchase Bills & Invoices",
          subtitle: "View, filter, edit and settle purchase bills",
          route: "/PurchaseList",
          icon: "mdi-format-list-bulleted",
          iconBg: "#fff7ed",
          iconColor: "#9a6b27",
          badge: "Purchase",
        },
        {
          type: "page",
          title: "Issue & Receipt",
          subtitle: "Manage metal issues and receipts for karigars & refiners",
          route: "/IssueReceipt",
          icon: "mdi-swap-horizontal",
          iconBg: "#f0f9ff",
          iconColor: "#0284c7",
          badge: "Stock",
        },
        {
          type: "page",
          title: "Conversion Entry",
          subtitle: "Process melting, touch purity and metal conversion",
          route: "/Conversion",
          icon: "mdi-autorenew",
          iconBg: "#fefce8",
          iconColor: "#ca8a04",
          badge: "Process",
        },
        {
          type: "page",
          title: "Stock Valuation",
          subtitle: "Real-time inventory valuation by purity and net weight",
          route: "/StockValuation",
          icon: "mdi-scale-balance",
          iconBg: "#f0fdf4",
          iconColor: "#16a34a",
          badge: "Inventory",
        },
        {
          type: "page",
          title: "Customer Directory",
          subtitle: "Customer contacts, KYC and ledger statements",
          route: "/Customer",
          icon: "mdi-account-heart-outline",
          iconBg: "#fdf2f8",
          iconColor: "#db2777",
          badge: "Customers",
        },
        {
          type: "page",
          title: "Supplier Directory",
          subtitle: "Wholesalers, bullion dealers and refinery contacts",
          route: "/Supplier",
          icon: "mdi-truck-outline",
          iconBg: "#ecfeff",
          iconColor: "#0891b2",
          badge: "Suppliers",
        },
        {
          type: "page",
          title: "Boarding Rates Master",
          subtitle: "Live Gold, Silver, Thankam, Platinum & Diamond market rates",
          route: "/BoardingRate",
          icon: "mdi-currency-inr",
          iconBg: "#fffbeb",
          iconColor: "#d97706",
          badge: "Rates",
        },
      ],
      systemDirectory: [
        {
          type: "page",
          title: "New Purchase Entry",
          subtitle: "Record jewelry inventory stock, supplier invoice details & rates",
          route: "/Purchase",
          icon: "mdi-cart-plus",
          iconBg: "#fff7ed",
          iconColor: "#9a6b27",
          badge: "Purchase",
          keywords: ["purchase", "new purchase", "buy", "stock entry", "add item", "bill", "inventory", "entry", "g111"],
        },
        {
          type: "page",
          title: "Purchase Bills & Invoices",
          subtitle: "View, filter, edit and settle all supplier purchase bills",
          route: "/PurchaseList",
          icon: "mdi-format-list-bulleted",
          iconBg: "#fff7ed",
          iconColor: "#9a6b27",
          badge: "Purchase",
          keywords: ["purchase list", "bills", "invoices", "purchase history", "supplier bills", "purchases", "settle"],
        },
        {
          type: "page",
          title: "Issue & Receipt",
          subtitle: "Manage metal issue and receipts for karigars and refiners",
          route: "/IssueReceipt",
          icon: "mdi-swap-horizontal",
          iconBg: "#f0f9ff",
          iconColor: "#0284c7",
          badge: "Stock",
          keywords: ["issue", "receipt", "metal issue", "metal receipt", "karigar issue", "transfer", "inward", "outward"],
        },
        {
          type: "page",
          title: "Issue & Receipt History",
          subtitle: "Track all historical metal issues and receipts",
          route: "/IssueReceiptList",
          icon: "mdi-history",
          iconBg: "#f0f9ff",
          iconColor: "#0284c7",
          badge: "Stock",
          keywords: ["issue receipt list", "receipt list", "metal history"],
        },
        {
          type: "page",
          title: "Conversion Entry",
          subtitle: "Process gold/silver metal melting, refining & touch conversion",
          route: "/Conversion",
          icon: "mdi-autorenew",
          iconBg: "#fefce8",
          iconColor: "#ca8a04",
          badge: "Process",
          keywords: ["conversion", "melting", "touch", "purity", "refining", "alloy"],
        },
        {
          type: "page",
          title: "Conversion List",
          subtitle: "History of metal melting and touch conversions",
          route: "/ConversionList",
          icon: "mdi-format-list-numbered",
          iconBg: "#fefce8",
          iconColor: "#ca8a04",
          badge: "Process",
          keywords: ["conversion list", "melting history"],
        },
        {
          type: "page",
          title: "Boarding Rates Master",
          subtitle: "Live Gold, Silver, Thankam, Platinum & Diamond market rates",
          route: "/BoardingRate",
          icon: "mdi-currency-inr",
          iconBg: "#fffbeb",
          iconColor: "#d97706",
          badge: "Rates",
          keywords: ["boarding rate", "gold rate", "rate", "silver rate", "thankam rate", "platinum", "diamond", "today rate", "price"],
        },
        {
          type: "page",
          title: "Stock Valuation",
          subtitle: "Real-time inventory valuation by metal purity, weight & cost",
          route: "/StockValuation",
          icon: "mdi-scale-balance",
          iconBg: "#f0fdf4",
          iconColor: "#16a34a",
          badge: "Inventory",
          keywords: ["stock", "valuation", "inventory", "holdings", "gross weight", "net weight", "value"],
        },
        {
          type: "page",
          title: "Scan Item (QR / Barcode)",
          subtitle: "Quick item lookup via camera or handheld barcode scanner",
          route: "/ScanItem",
          icon: "mdi-qrcode-scan",
          iconBg: "#faf5ff",
          iconColor: "#7c3aed",
          badge: "Tools",
          keywords: ["scan", "qr", "barcode", "tag", "lookup", "rfid"],
        },
        {
          type: "page",
          title: "Customer Directory",
          subtitle: "Customer contact management, KYC and ledger records",
          route: "/Customer",
          icon: "mdi-account-heart-outline",
          iconBg: "#fdf2f8",
          iconColor: "#db2777",
          badge: "Customers",
          keywords: ["customer", "client", "buyers", "contacts", "directory", "phone"],
        },
        {
          type: "page",
          title: "Customer Statements",
          subtitle: "Customer debit/credit ledgers, purchases and balances",
          route: "/CustomerStatement",
          icon: "mdi-file-document-outline",
          iconBg: "#fdf2f8",
          iconColor: "#db2777",
          badge: "Customers",
          keywords: ["statement", "customer statement", "ledger", "balance", "dues"],
        },
        {
          type: "page",
          title: "Supplier Management",
          subtitle: "Wholesalers, vendors, bullions & refinery contact book",
          route: "/Supplier",
          icon: "mdi-truck-outline",
          iconBg: "#ecfeff",
          iconColor: "#0891b2",
          badge: "Suppliers",
          keywords: ["supplier", "vendor", "wholesaler", "bullion", "dealer", "contacts"],
        },
        {
          type: "page",
          title: "Supplier Settlements",
          subtitle: "Settle pending metal weight and cash balances with suppliers",
          route: "/supplierSettlement",
          icon: "mdi-cash-check",
          iconBg: "#ecfeff",
          iconColor: "#0891b2",
          badge: "Suppliers",
          keywords: ["supplier settlement", "settle", "supplier payment", "balance"],
        },
        {
          type: "page",
          title: "Karigar Management",
          subtitle: "Manage goldsmiths, job allotments and labor charges",
          route: "/KarigarManagement",
          icon: "mdi-hammer-wrench",
          iconBg: "#f1f5f9",
          iconColor: "#475569",
          badge: "Workshop",
          keywords: ["karigar", "goldsmith", "smith", "craftsman", "labor", "workshop"],
        },
        {
          type: "page",
          title: "Karigar Jobs & Work Orders",
          subtitle: "Active jewelry manufacturing jobs and work orders",
          route: "/KarigarJobs",
          icon: "mdi-briefcase-outline",
          iconBg: "#f1f5f9",
          iconColor: "#475569",
          badge: "Workshop",
          keywords: ["karigar jobs", "work orders", "job card", "manufacturing"],
        },
        {
          type: "page",
          title: "Repair & Service Jobs",
          subtitle: "Customer jewelry repair intake, polishing, stone fixing & delivery",
          route: "/RepairJobs",
          icon: "mdi-wrench-outline",
          iconBg: "#fff7ed",
          iconColor: "#ea580c",
          badge: "Services",
          keywords: ["repair", "service", "polishing", "soldering", "fixing", "stone setting"],
        },
        {
          type: "page",
          title: "Category Master",
          subtitle: "Jewelry categories (Gold, Platinum, Silver, Diamond)",
          route: "/Category",
          icon: "mdi-shape-outline",
          iconBg: "#f5f3ff",
          iconColor: "#8b5cf6",
          badge: "Masters",
          keywords: ["category", "categories", "gold 1", "platinum", "silver", "diamond", "master"],
        },
        {
          type: "page",
          title: "Account Heads",
          subtitle: "Chart of accounts and financial accounting heads",
          route: "/AccountHeads",
          icon: "mdi-clipboard-text-outline",
          iconBg: "#f0fdfa",
          iconColor: "#0d9488",
          badge: "Accounting",
          keywords: ["account heads", "chart of accounts", "heads", "ledgers"],
        },
        {
          type: "page",
          title: "Accounts & Daybook",
          subtitle: "Debit and credit vouchers, receipts, journal entries",
          route: "/Accounts",
          icon: "mdi-card-account-details-outline",
          iconBg: "#f0fdfa",
          iconColor: "#0d9488",
          badge: "Accounting",
          keywords: ["accounts", "daybook", "journal", "vouchers", "receipts", "payments"],
        },
        {
          type: "page",
          title: "Trial Balance",
          subtitle: "Real-time financial trial balance reports",
          route: "/TrialBalance",
          icon: "mdi-scale-balance",
          iconBg: "#f0fdfa",
          iconColor: "#0d9488",
          badge: "Financials",
          keywords: ["trial balance", "financial statement", "debit credit balance"],
        },
        {
          type: "page",
          title: "Profit & Loss Statement",
          subtitle: "Revenue, gross margins, operating expenses & net profit",
          route: "/ProfitAndLoss",
          icon: "mdi-chart-line",
          iconBg: "#f0fdf4",
          iconColor: "#16a34a",
          badge: "Financials",
          keywords: ["profit and loss", "p&l", "revenue", "income", "expenses", "margins"],
        },
        {
          type: "page",
          title: "Balance Sheet",
          subtitle: "Assets, liabilities and shareholder equity",
          route: "/BalanceSheet",
          icon: "mdi-bank-outline",
          iconBg: "#f0fdf4",
          iconColor: "#16a34a",
          badge: "Financials",
          keywords: ["balance sheet", "assets", "liabilities", "equity", "net worth"],
        },
        {
          type: "page",
          title: "User Profile & Settings",
          subtitle: "Business info, GSTIN, invoice settings & password",
          route: "/userProfile",
          icon: "mdi-account-cog-outline",
          iconBg: "#f8fafc",
          iconColor: "#475569",
          badge: "Settings",
          keywords: ["profile", "user profile", "settings", "gstin", "password", "store settings"],
        },
      ],
    };
  },
  beforeMount() {
    this.userNavItemsStaffAdmin = localStorage.getItem("menu");

    // console.log("menu", this.userNavItemsStaffAdmin);

  },
  computed: {
    staffMenuHasDashboard() {
      return (this.navItems || []).some((m) => m && m.menu && m.menu.route === "/StaffDashboard");
    },
    appUser() {
      return this.$store.state.userData;
    },
    appType() {
      return this.$store.state.userType;
    },
    isSuperAdmin() {
      return this.appType === "SuperAdmin";
    },
    userNavItemsStaffAdmin1(){
      return this.$store.state.menu;

    },
    navItems() {
      if (this.appType == "SuperAdmin") return this.userNavItemsSuperAdmin;
      else if (this.appType == "JewelleryAdmin")
        return this.userNavItemsJuwelAdmin;
      else if (this.appType == "Staff") return this.userNavItemsStaffAdmin1;
      else return this.userNavItems;
    },
    appMenu() {
      // console.log("menus", store.state.menus);
      return this.$store.state.menus;
    },
    appLogin() {
      return false;
    },
    // appType() {
    //   return this.$store.state.userType;
    // },
    appNavItems() {
      return this.$store.state.menus;
    },
    GSTstatus(){
      // return this.$store.state.GSTstatus;
      return localStorage.getItem("GSTstatus")
    },
    dashboardRoute() {
      if (this.appType === 'SuperAdmin') return '/superadminDashboard';
      if (this.appType === 'JewelleryAdmin') return '/JewelleryDashboard';
      return '/StaffDashboard';
    },
    currentDayName() {
      return new Date().toLocaleDateString("en-US", { weekday: "long" });
    },
    currentFormattedDate() {
      const d = new Date();
      const day = d.getDate();
      const month = d.toLocaleDateString("en-US", { month: "short" });
      const year = d.getFullYear();
      return `${day} ${month} ${year}`;
    },
    filteredRates() {
      if (!this.searchQuery) return [];
      const q = this.searchQuery.toLowerCase().trim();
      const matchRate = ["gold", "silver", "thankam", "platinum", "diamond", "rate", "price", "today"].some((k) => q.includes(k));
      if (!matchRate) return [];

      const rates = [
        {
          type: "rate",
          metal: "gold",
          title: "Gold Boarding Rate",
          subtitle: "Standard gold boarding rate today",
          rate: "₹" + this.getStoredRate("goldRate", "10,000") + "/g",
          route: "/BoardingRate",
        },
        {
          type: "rate",
          metal: "thankam",
          title: "Thankam (24K Pure) Rate",
          subtitle: "Prevailing Thankam boarding rate",
          rate: "₹" + this.getStoredRate("thankamRate", "10,000") + "/g",
          route: "/BoardingRate",
        },
        {
          type: "rate",
          metal: "silver",
          title: "Silver Boarding Rate",
          subtitle: "Prevailing silver rate today",
          rate: "₹" + this.getStoredRate("silverRate", "10,000") + "/g",
          route: "/BoardingRate",
        },
        {
          type: "rate",
          metal: "platinum",
          title: "Platinum Boarding Rate",
          subtitle: "Prevailing platinum rate today",
          rate: "₹" + this.getStoredRate("platinumRate", "10,000") + "/g",
          route: "/BoardingRate",
        },
        {
          type: "rate",
          metal: "diamond",
          title: "Diamond Boarding Rate",
          subtitle: "Standard diamond rate per carat",
          rate: "₹" + this.getStoredRate("diamond", "1,000") + "/ct",
          route: "/BoardingRate",
        },
      ];

      return rates.filter(
        (r) =>
          r.title.toLowerCase().includes(q) ||
          r.metal.includes(q) ||
          q.includes("rate") ||
          q.includes("price") ||
          q.includes("today")
      );
    },
    filteredPages() {
      if (!this.searchQuery) return [];
      const q = this.searchQuery.toLowerCase().trim();
      return this.systemDirectory.filter((p) => {
        return (
          p.title.toLowerCase().includes(q) ||
          p.subtitle.toLowerCase().includes(q) ||
          p.route.toLowerCase().includes(q) ||
          p.badge.toLowerCase().includes(q) ||
          (p.keywords && p.keywords.some((k) => k.includes(q)))
        );
      });
    },
    flattenedResults() {
      if (!this.searchQuery.trim()) {
        return this.quickSuggestions;
      }
      return [
        ...this.filteredRates,
        ...this.liveBills,
        ...this.liveSuppliers,
        ...this.liveCustomers,
        ...this.filteredPages,
      ];
    },
  },
  mounted() {
    window.addEventListener("keydown", this.handleGlobalKeydown);
    document.addEventListener("click", this.handleClickOutside);
  },
  beforeDestroy() {
    window.removeEventListener("keydown", this.handleGlobalKeydown);
    document.removeEventListener("click", this.handleClickOutside);
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
    }
  },
  watch: {
    GSTstatus() {
      this.newGSTstatus = JSON.parse(this.GSTstatus);
      console.log("newGSTstatus==", this.newGSTstatus);
    },
  },
  methods: {
    getMenuIcon(name) {
      if (!name) return 'mdi-circle-small';
      const map = {
        'DASHBOARD': 'mdi-home',
        'PURCHASE': 'mdi-cart-outline',
        'ISSUE & RECEIPT': 'mdi-swap-horizontal',
        'CONVERSION': 'mdi-autorenew',
        'ACCOUNT HEADS': 'mdi-clipboard-text-outline',
        'ACCOUNTS': 'mdi-card-account-details-outline',
        'TRIAL BALANCE': 'mdi-scale-balance',
        'PROFIT & LOSS': 'mdi-chart-line',
        'BALANCE SHEET': 'mdi-clipboard-text-outline',
        'ESTIMATE': 'mdi-tag-outline',
        'BILLING': 'mdi-file-outline',
        'STOCK': 'mdi-package-variant-closed',
        'SUPPLIER': 'mdi-truck-outline',
        'CATEGORY': 'mdi-shape-outline',
        'HSN': 'mdi-file-document-outline',
        'GOLD SCHEME': 'mdi-circle-multiple-outline',
        'SCHEME LIST': 'mdi-bookmark-outline',
        'STAFF': 'mdi-account-group-outline',
        'CUSTOMER': 'mdi-account-heart-outline',
        'SALES HISTORY': 'mdi-history',
        'BOARDING RATE': 'mdi-currency-inr',
        'OLD GOLD': 'mdi-gold',
        'REPORT': 'mdi-chart-bar',
        'REPORTS': 'mdi-chart-bar',
        'MASTERS': 'mdi-database-outline',
        'SETTINGS': 'mdi-cog-outline',
      };
      return map[name.toUpperCase()] || 'mdi-circle-small';
    },
    formatMenuName(name) {
      if (!name) return '';
      return name
        .split(' ')
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
        .join(' ');
    },
    hasSubItems(main) {
      const m = main && main.menu;
      return !!(m && ((m.subMenu && m.subMenu.length) || (m.subMenuCategories && m.subMenuCategories.length)));
    },
    isMobileGroupActive(main) {
      const m = main.menu;
      if (m.subMenu && m.subMenu.some((s) => s.route === this.$route.path)) return true;
      if (m.subMenuCategories) {
        return m.subMenuCategories.some((c) => c.reports && c.reports.some((r) => r.route === this.$route.path));
      }
      return this.isRouteActive(m.route);
    },
    isRouteActive(route) {
      if (!route) return false;
      if (this.$route.path === route) return true;
      if (
        route === '/PurchaseList' &&
        this.$route.path.toLowerCase().includes('purchase')
      )
        return true;
      if (
        (route === '/StaffDashboard' ||
          route === '/superadminDashboard' ||
          route === '/JewelleryDashboard') &&
        this.$route.path.toLowerCase().includes('dashboard')
      )
        return true;
      if (
        route === '/SalesList' &&
        (this.$route.path.toLowerCase().includes('sales') ||
          this.$route.path.toLowerCase().includes('billing'))
      )
        return true;
      return false;
    },
    openSearch() {
      this.isSearchOpen = true;
      this.selectedSearchIndex = 0;
    },
    closeSearch() {
      this.isSearchOpen = false;
      this.selectedSearchIndex = -1;
      if (this.$refs.headerSearchInput) {
        this.$refs.headerSearchInput.blur();
      }
    },
    clearSearch() {
      this.searchQuery = '';
      this.liveBills = [];
      this.liveSuppliers = [];
      this.liveCustomers = [];
      this.selectedSearchIndex = 0;
      if (this.$refs.headerSearchInput) {
        this.$refs.headerSearchInput.focus();
      }
    },
    onSearchInput() {
      this.isSearchOpen = true;
      this.selectedSearchIndex = 0;
      if (this.searchDebounceTimer) {
        clearTimeout(this.searchDebounceTimer);
      }
      const q = this.searchQuery.trim();
      if (q.length >= 2) {
        this.searchDebounceTimer = setTimeout(() => {
          this.fetchLiveSearchResults();
        }, 250);
      } else {
        this.liveBills = [];
        this.liveSuppliers = [];
        this.liveCustomers = [];
      }
    },
    fetchLiveSearchResults() {
      const token = localStorage.getItem('token');
      if (!token) return;
      const q = this.searchQuery.trim();
      if (q.length < 2) return;

      this.isSearchingLive = true;

      // 1. Search Purchase Bills & Invoices
      const billReq = axios
        .get('/purchaseitem/getalllist', {
          headers: { 'x-auth-token': token },
          params: { keyword: q, count: 5, page: 1 },
        })
        .then((res) => {
          if (res.data && res.data.status && Array.isArray(res.data.data)) {
            this.liveBills = res.data.data.map((b) => ({
              type: 'bill',
              id: b._id,
              title:
                `Bill #${b.billPrefix ? b.billPrefix + '-' : ''}${b.billNo || 'N/A'}` +
                (b.invoiceNo ? ` (Inv #${b.invoiceNo})` : ''),
              subtitle: `${
                b.supplierId && b.supplierId.supplierName
                  ? b.supplierId.supplierName
                  : 'Supplier'
              } • ${this.formatSearchDate(b.purchaseDate)}`,
              isSettled: b.isSettled,
              route: `/purchaseDetails?id=${b._id}`,
            }));
          }
        })
        .catch(() => {});

      // 2. Search Suppliers
      const supReq = axios
        .get('/supplier/getlist', {
          headers: { 'x-auth-token': token },
          params: { keyword: q, count: 4, page: 1 },
        })
        .then((res) => {
          if (res.data && res.data.status && Array.isArray(res.data.data)) {
            this.liveSuppliers = res.data.data.map((s) => ({
              type: 'supplier',
              id: s._id,
              title: s.supplierName,
              subtitle: `Code: ${s.supplierCode || '-'} • Contact: ${
                s.contactNumber || '-'
              }`,
              route: '/Supplier',
            }));
          }
        })
        .catch(() => {});

      // 3. Search Customers
      const custReq = axios
        .get('/customer/getlist', {
          headers: { 'x-auth-token': token },
          params: { keyword: q, limit: 4, page: 1 },
        })
        .then((res) => {
          if (res.data && res.data.status && Array.isArray(res.data.data)) {
            this.liveCustomers = res.data.data.map((c) => ({
              type: 'customer',
              id: c._id,
              title: c.customerName,
              subtitle: `Mobile: ${c.mobileNumber || '-'} • ${
                c.city ? c.city : 'Customer'
              }`,
              route: '/Customer',
            }));
          }
        })
        .catch(() => {});

      Promise.allSettled([billReq, supReq, custReq]).finally(() => {
        this.isSearchingLive = false;
      });
    },
    navigateResults(delta) {
      const total = this.flattenedResults.length;
      if (total === 0) return;
      this.selectedSearchIndex =
        (this.selectedSearchIndex + delta + total) % total;
    },
    selectCurrentResult() {
      if (
        this.flattenedResults.length > 0 &&
        this.selectedSearchIndex >= 0 &&
        this.selectedSearchIndex < this.flattenedResults.length
      ) {
        this.executeResult(this.flattenedResults[this.selectedSearchIndex]);
      }
    },
    executeResult(item) {
      if (!item) return;
      this.closeSearch();
      if (item.route) {
        if (this.$route.fullPath !== item.route) {
          this.$router.push(item.route);
        }
      }
    },
    navigateTo(route) {
      this.closeSearch();
      if (this.$route.fullPath !== route) {
        this.$router.push(route);
      }
    },
    formatSearchDate(dateStr) {
      if (!dateStr) return '';
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return '';
      return d.toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      });
    },
    getStoredRate(key, fallback) {
      const val = localStorage.getItem(key);
      return val && val !== 'null'
        ? Number(val).toLocaleString('en-IN')
        : fallback;
    },
    getResultIndex(item) {
      return this.flattenedResults.indexOf(item);
    },
    handleGlobalKeydown(e) {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        if (this.$refs.headerSearchInput) {
          this.$refs.headerSearchInput.focus();
          this.openSearch();
        }
      }
    },
    handleClickOutside(e) {
      if (
        this.$refs.searchContainer &&
        !this.$refs.searchContainer.contains(e.target)
      ) {
        this.closeSearch();
      }
    },
  },
};
</script>

<style>
.app-header-desktop {
  background: #ffffff;
  border-bottom: 1px solid #eeebe5;
}

.header-top-bar {
  border-bottom: 1px solid #f5f2ed;
}

.header-left {
  flex: 1;
  max-width: 580px;
}

.sidebar-toggle-btn {
  border-radius: 8px;
  transition: all 0.2s;
}

.sidebar-toggle-btn:hover {
  background: #fbf5eb;
}

/* Header Search Styles */
.header-search-wrapper {
  position: relative;
  flex: 1;
  max-width: 480px;
}

.header-search-container {
  display: flex;
  align-items: center;
  background: #fbfaf8;
  border: 1px solid #e7e2da;
  border-radius: 10px;
  padding: 6px 12px;
  width: 100%;
  transition: all 0.2s ease;
  position: relative;
}

.header-search-container.is-focused,
.header-search-container:focus-within {
  background: #ffffff;
  border-color: #9a6b27;
  box-shadow: 0 0 0 3px rgba(184, 106, 36, 0.12);
}

.header-search-field {
  border: none;
  outline: none;
  background: transparent;
  font-family: 'Plus Jakarta Sans', 'Inter', sans-serif;
  font-size: 13px;
  color: #0f172a;
  width: 100%;
  min-width: 0;
}

.header-search-field::placeholder {
  color: #94a3b8;
  font-size: 12.5px;
}

.search-kbd-shortcut {
  font-family: 'Inter', sans-serif;
  font-size: 10px;
  font-weight: 600;
  color: #8d7c61;
  background: #f1ede5;
  border: 1px solid #dfd7cc;
  padding: 2px 6px;
  border-radius: 4px;
  white-space: nowrap;
}

.clear-search-btn {
  width: 20px !important;
  height: 20px !important;
}

/* Command Palette Popover */
.search-results-popover {
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  width: 540px;
  max-width: calc(100vw - 32px);
  max-height: 480px;
  overflow-y: auto;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  box-shadow: 0 20px 40px -8px rgba(15, 23, 42, 0.16), 0 4px 16px -2px rgba(15, 23, 42, 0.08);
  z-index: 1000;
  padding: 8px 6px 4px 6px;
  text-align: left;
  font-family: 'Plus Jakarta Sans', 'Inter', sans-serif;
}

.search-results-popover::-webkit-scrollbar {
  width: 5px;
}
.search-results-popover::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 10px;
}

.search-group-header {
  font-size: 10.5px;
  font-weight: 700;
  color: #94a3b8;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  padding: 6px 12px 4px 12px;
}

.search-group-header .hint-text {
  font-size: 10px;
  font-weight: 500;
  color: #94a3b8;
  text-transform: none;
  letter-spacing: 0;
}

.search-result-item {
  padding: 8px 12px;
  border-radius: 9px;
  cursor: pointer;
  transition: all 0.12s ease;
  margin: 1px 0;
}

.search-result-item:hover,
.search-result-item.is-active {
  background-color: #fff7ed;
}

.result-icon-pill {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.result-text-block {
  min-width: 0;
}

.result-title {
  font-size: 13.5px;
  font-weight: 600;
  color: #1e293b;
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.search-result-item:hover .result-title,
.search-result-item.is-active .result-title {
  color: #9a6b27;
}

.result-subtitle {
  font-size: 11.5px;
  color: #64748b;
  margin-top: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.result-badge {
  font-size: 11px;
  font-weight: 600;
  color: #64748b;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  padding: 2px 8px;
  border-radius: 6px;
  white-space: nowrap;
}

.badge-count {
  font-size: 10px;
  font-weight: 700;
  color: #64748b;
  background: #f1f5f9;
  padding: 1px 6px;
  border-radius: 10px;
}

.badge-rate {
  background: #fffbeb !important;
  color: #b45309 !important;
  border-color: #fde68a !important;
}

.badge-supplier {
  background: #ecfeff !important;
  color: #0e7490 !important;
  border-color: #cffafe !important;
}

.badge-customer {
  background: #fdf2f8 !important;
  color: #be185d !important;
  border-color: #fce7f3 !important;
}

.result-rate-val {
  font-size: 13px;
  color: #b45309;
}

.result-status-pill {
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 6px;
}

.status-settled {
  background-color: #f0fdf4;
  color: #15803d;
  border: 1px solid #bbf7d0;
}

.status-unsettled {
  background-color: #fffbeb;
  color: #b45309;
  border: 1px solid #fef3c7;
}

.rates-chips-row {
  padding: 2px 10px;
}

.rate-chip {
  background: #fbfaf8;
  border: 1px solid #e7e2da;
  border-radius: 8px;
  padding: 4px 10px;
  font-size: 11.5px;
  cursor: pointer;
  transition: all 0.15s ease;
}

.rate-chip:hover {
  border-color: #9a6b27;
  background: #fff7ed;
}

.rate-chip-metal {
  font-weight: 600;
  color: #64748b;
  margin-right: 4px;
}

.rate-chip-price {
  font-weight: 700;
  color: #1e293b;
}

.rate-chip:hover .rate-chip-price {
  color: #9a6b27;
}

.search-popover-footer {
  background: #ffffff;
  border-top: 1px solid #f1f5f9;
  margin: 4px -6px -4px -6px;
  padding: 8px 12px !important;
  border-radius: 0 0 14px 14px;
}

.footer-hints {
  gap: 10px;
}

.kbd-hint {
  font-size: 11px;
  color: #64748b;
}

.kbd-hint kbd {
  background: #f1f5f9;
  border: 1px solid #cbd5e1;
  border-radius: 4px;
  padding: 1px 5px;
  font-size: 10px;
  font-weight: 600;
  color: #334151;
  margin-right: 2px;
}

.search-empty-icon {
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.search-empty-title {
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
}

.search-empty-sub {
  font-size: 12px;
  color: #64748b;
  margin-top: 3px;
}

.search-popover-fade-enter-active,
.search-popover-fade-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}

.search-popover-fade-enter,
.search-popover-fade-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

.header-calendar-pill {
  background: #ffffff;
  border: 1px solid #ebe6df;
  border-radius: 10px;
  padding: 4px 12px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.02);
}

.calendar-day-name {
  font-family: 'Inter', sans-serif;
  font-size: 10px;
  font-weight: 700;
  color: #64748b;
  line-height: 1.1;
  text-transform: capitalize;
}

.calendar-full-date {
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 700;
  color: #172238;
  line-height: 1.1;
}

.notification-bell-btn {
  border-radius: 50%;
  transition: background 0.2s;
}

.notification-bell-btn:hover {
  background: #fbf5eb;
}
input::-webkit-input-placeholder {
  color: #c5c5c5 !important;
  font-size: 14px !important;
  font-family: kumbhRegular !important;
}
input::-moz-placeholder {
  color: #c5c5c5 !important;
  font-size: 14px !important;
  font-family: kumbhRegular !important;
}
.searchBox .v-input__control {
  min-height: 10px !important;
}
.searchBox .v-input__control .v-input__slot {
  padding: 0px !important;
}
.v-list-group__header__append-icon {
  display: none !important;
}

@media only screen and (min-device-width: 767px) {
  .fixedSidebar {
    /* position: fixed; */

    position: -webkit-sticky;
    /* Safari */
    position: sticky;
    top: 50;
  }
}

@media only screen and (min-device-width: 360px) and (max-device-width: 640px) {
  .fixedSidebar {
    display: none !important;
  }
}

.activated {
  color: #9a6b27 !important;
}

.deactivated {
  color: #64748b !important;
}

.demo-badge-header {
  font-size: 11px !important;
  background: rgba(154, 107, 39, 0.1) !important;
  border: 1px solid rgba(154, 107, 39, 0.3) !important;
  color: #9a6b27 !important;
  padding: 2px 6px !important;
  border-radius: 4px !important;
  font-weight: 700 !important;
  vertical-align: middle !important;
  margin-left: 6px !important;
  letter-spacing: 0.5px !important;
  display: inline-block !important;
  line-height: 1 !important;
}

.demo-badge-sidebar {
  font-size: 11px !important;
  background: rgba(255, 255, 255, 0.15) !important;
  border: 1px solid rgba(255, 255, 255, 0.4) !important;
  color: #ffffff !important;
  padding: 2px 6px !important;
  border-radius: 4px !important;
  font-weight: 700 !important;
  vertical-align: middle !important;
  margin-left: 6px !important;
  letter-spacing: 0.5px !important;
  display: inline-block !important;
  line-height: 1 !important;
}

/* Mobile Header & Navigation Drawer */
.mobile-app-bar {
  border-bottom: 1px solid #f0decb !important;
  background-color: #ffffff !important;
}

.mobile-logo-text {
  font-family: 'Cinzel', 'Plus Jakarta Sans', serif;
  font-size: 20px;
  font-weight: 700;
  color: #172238 !important;
  letter-spacing: 2px;
}

.mobile-logo-text .gold-text {
  color: #9a6b27 !important;
}

.mobile-rates-strip-container {
  background-color: #fbfaf8;
  border-bottom: 1px solid #f1ece5;
}

.mobile-nav-drawer {
  background:
    url('../assets/images/sidebar_wave.svg') no-repeat bottom right / 100% auto,
    linear-gradient(180deg, #fffdfa 0%, #fff7ee 35%, #faecd8 75%, #f6debf 100%) !important;
  background-color: #fff7ee !important;
  font-family: 'Plus Jakarta Sans', sans-serif !important;
}

.mobile-nav-drawer .v-navigation-drawer__content {
  background: transparent !important;
  display: flex !important;
  flex-direction: column !important;
}

.mobile-nav-drawer .v-list,
.mobile-nav-drawer .v-list-item {
  background: transparent !important;
}
</style>
